/**
 * Wire types for the Drive For Me admin API.
 *
 * These mirror the Java DTOs in `com.opriontravel.driveforme.dto.admin` and the
 * auth response one-for-one. Keeping them in a single file makes a backend
 * change a compile error here rather than a runtime surprise on a screen.
 */

/* -------------------------------------------------------------------- auth */

export interface AuthResponse {
  token: string
  userId: number
  name: string
  email: string | null
  phone: string | null
  role: string
  profileImageUrl: string | null
  enabled: boolean | null
  emailVerified: boolean | null
  phoneVerified: boolean | null
  requiresPhoneVerification: boolean | null
  createdAt: string | null
}

export interface LoginRequest {
  identifier: string
  password: string
}

/* -------------------------------------------------------------- pagination */

export interface PageResponse<T> {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
}

export type SortDirection = 'asc' | 'desc'

export interface PageParams {
  page?: number
  size?: number
  sort?: string
  direction?: SortDirection
}

/* ------------------------------------------------------------------ people */

export interface AdminUser {
  id: number
  name: string | null
  email: string | null
  phone: string | null
  profileImageUrl: string | null
  role: string | null
  authProvider: string | null
  enabled: boolean
  phoneVerified: boolean
  emailVerified: boolean
  createdAt: string | null
  updatedAt: string | null
}

export interface AdminDriver {
  id: number
  name: string | null
  email: string | null
  phone: string | null
  profileImageUrl: string | null
  enabled: boolean
  phoneVerified: boolean
  activeTripCount: number
  /**
   * What customers think, across every rated trip. Null until the first rating
   * — "0.0" would read as the worst score rather than as no score — and always
   * beside `ratingCount`, because one five-star trip is a perfect average.
   */
  averageRating: number | null
  ratingCount: number
  createdAt: string | null
  updatedAt: string | null
  /**
   * The driver's account on the Option Travel (OTS) driver app, which Drive
   * For Me trips are worked on. Null until the driver sync links the two —
   * such a driver cannot be given a trip while the integration is on.
   */
  otsDriverId: number | null
  /** The OTS driver code ops know them by. */
  otsDriverCode: string | null
  /** Terminated in OTS; the sync suspended this account because of it. */
  otsTerminated: boolean
}

export interface DriverOption {
  id: number
  name: string | null
  phone: string | null
  profileImageUrl: string | null
  activeTripCount: number
  /** Has an OTS driver-app account; an unlinked driver cannot be assigned while the integration is on. */
  otsLinked: boolean
  otsDriverCode: string | null
}

/** What one run of the OTS driver sync changed. */
export interface OtsDriverSyncResult {
  fetched: number
  created: number
  linked: number
  updated: number
  deactivated: number
  reactivated: number
  /** Drivers the sync could not match safely, in operator wording. */
  conflicts: string[]
}

export interface PeopleFilters extends PageParams {
  search?: string
  enabled?: boolean
  phoneVerified?: boolean
  createdFrom?: string
  createdTo?: string
}

/* ---------------------------------------------------------- trip requests */

export const TRIP_REQUEST_STATUSES = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'] as const
export type TripRequestStatus = (typeof TRIP_REQUEST_STATUSES)[number]

export const SERVICE_TYPES = ['CITY', 'TRAVEL'] as const
export type ServiceType = (typeof SERVICE_TYPES)[number]

export const REQUEST_PAYMENT_STATUSES = ['UNPAID', 'PAID', 'REFUNDED', 'FAILED', 'PENDING_PAYMENT'] as const
export type RequestPaymentStatus = (typeof REQUEST_PAYMENT_STATUSES)[number]

/* ---------------------------------------------------------------- payments */

export const PAYMENT_METHODS = ['CARD', 'CASH', 'INSTAPAY'] as const
export type PaymentMethodCode = (typeof PAYMENT_METHODS)[number]

export const PAYMENT_VERIFICATION_STATUSES = [
  'NOT_REQUIRED',
  'PENDING',
  'VERIFIED',
  'REJECTED',
] as const
export type PaymentVerificationStatus = (typeof PAYMENT_VERIFICATION_STATUSES)[number]

/**
 * How one booking was paid for. Nested identically on trips and trip requests,
 * so both screens render it through the same component and cannot drift.
 *
 * Every amount arrives twice: `*Minor` is the integer piastre figure the server
 * computed, and the plain field is the same value as a decimal for display. The
 * dashboard never adds or subtracts money — if a figure is needed, the server
 * has already sent it.
 *
 * All fields tolerate a booking made before any of this existed: `pointsUsed`
 * is zero on those rows, `externalAmount` is the whole charge, and
 * `verificationStatus` is `NOT_REQUIRED`.
 */
export interface AdminPaymentSummary {
  /** The raw stored value, which may be any historical spelling. Do not switch on it. */
  paymentMethod: string | null
  /** The stable machine value. Null for a value the backend has never written. */
  paymentMethodCode: PaymentMethodCode | null
  paymentStatus: string | null

  totalAmountMinor: number
  tripAmountMinor: number
  penaltyAmountMinor: number
  pointsUsedMinor: number
  externalAmountMinor: number

  totalAmount: number
  tripAmount: number
  penaltyAmount: number
  pointsUsed: number
  externalAmount: number

  currency: string

  fullyCoveredByPoints: boolean
  partiallyCoveredByPoints: boolean

  verificationStatus: PaymentVerificationStatus | null
  verificationComment: string | null
  verifiedByAdminUserId: number | null
  verifiedByAdminName: string | null
  verifiedAt: string | null
  /**
   * Whether a manual decision is permitted. False for card, whose status is the
   * gateway's. The server enforces the same rule, so this only decides what is
   * rendered — it never authorises anything.
   */
  canVerify: boolean

  hasProof: boolean
  proofUploadedAt: string | null
  proofContentType: string | null
  proofSizeBytes: number | null

  /**
   * Whether an operator may switch this booking from credit card to Instapay.
   *
   * True only for a live card booking whose payment is still pending and which
   * carries no gateway transaction — the same conditions the endpoint enforces.
   * Computed on the server for the usual reason: the dashboard offers the action
   * on exactly the rows the server would accept it on, rather than working the
   * rule out again from a status string and drifting.
   */
  canConvertToInstapay: boolean
  /**
   * Whether an operator may attach a proof of payment themselves.
   *
   * False for card, which has no receipt to file against it. True wherever a
   * human settles the payment — the same rule as {@link canVerify}, and the two
   * move together.
   */
  canUploadProof: boolean
}

/** The same summary, fetched on its own for the verification panel. */
export interface PaymentVerificationDetails extends AdminPaymentSummary {
  tripRequestId: number
  bookingReference: string | null
}

/** One movement of a customer's points, as the details drawer lists them. */
export interface PointsTransaction {
  id: number
  userId: number
  /** Signed: positive credited the customer, negative spent. */
  amountMinor: number
  balanceAfterMinor: number
  amount: number
  balanceAfter: number
  currency: string
  type: 'REFUND_CREDIT' | 'BOOKING_REDEMPTION' | 'BOOKING_RESTORE' | 'ADMIN_ADJUSTMENT' | null
  credit: boolean
  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  paymentRefundId: number | null
  reason: string | null
  createdByAdminUserId: number | null
  createdByAdminName: string | null
  createdAt: string | null
}

export interface InstapayConfig {
  id: number | null
  bankName: string | null
  accountNumber: string | null
  accountHolderName: string | null
  instructions: string | null
  active: boolean
  /** False until every required field is filled in. The app hides Instapay until it is true. */
  usable: boolean
  updatedByAdminUserId: number | null
  updatedByAdminName: string | null
  updatedAt: string | null
}

export interface InstapayConfigUpdate {
  bankName: string
  accountNumber: string
  accountHolderName: string
  instructions?: string | null
  active: boolean
}

export interface PaymentVerificationRequest {
  comment?: string
}

/**
 * An operator switching how a booking is paid for.
 *
 * Carries the target method and why, and deliberately not the method being moved
 * *from*: the server reads that off the booking, so a stale row on screen cannot
 * talk it into a conversion it would otherwise refuse.
 *
 * `INSTAPAY` is the only value the API accepts today — see
 * `changePaymentMethod` for why the other directions are not supported.
 */
export interface PaymentMethodChangeRequest {
  method: PaymentMethodCode
  reason?: string
}

/** What the API answers after a proof image is stored. */
export interface PaymentProofUploadResponse {
  success: boolean
  tripRequestId: number
  /** Detected from the file's own bytes, not from what the browser declared. */
  contentType: string
  sizeBytes: number
  /** Always `PENDING`: an upload is evidence, not a payment. */
  verificationStatus: PaymentVerificationStatus
  message: string
}

/**
 * What a customer put on top of an inter-city route when they booked — a
 * return leg (there and back on one booking, priced at 1.5x the one-way fare)
 * and/or 3 or 6 extra hours with the driver, spent in Cairo or at the
 * destination and priced at that area's own 3- or 6-hour package.
 *
 * Nested identically on trips and trip requests, like `payment`, so both
 * screens read one shape. Always present: a city booking, or a travel booking
 * that chose nothing, arrives as `any: false` with zeros — which is also what
 * every booking made before add-ons existed looks like.
 *
 * The amounts are the server's, computed from the package when the booking was
 * made and stored with it. They are a breakdown of the fare, not an addition
 * to it — route and add-ons were paid for in one transaction, so `basePrice`
 * and `payment.tripAmount` already include them.
 *
 * Not the trip's *extension* hours (`AdminTrip.extraHoursCount`), which are
 * bought mid-journey and paid for separately.
 */
export interface TravelAddOns {
  any: boolean
  returnLeg: boolean
  /** 0, 3 or 6 (10 on bookings made before 2026-09-25). */
  extraHours: number
  /** Where the hours are spent, as the area's category id — null when none. */
  extraHoursAreaId: number | null
  /** That area's name — "Cairo" — or null when none were booked. */
  extraHoursArea: string | null
  returnLegAmountMinor: number
  extraHoursAmountMinor: number
  totalAmountMinor: number
  returnLegAmount: number
  extraHoursAmount: number
  totalAmount: number
  currency: string
  /** Worded by the server — "Return leg · +6 h in Cairo" — or null when none. */
  summary: string | null
}

/** One end of a Cairo trip: the sub-area it fell in and what that added. */
export interface AreaSurchargeEnd {
  /** Null when the point is in no sub-area. */
  subAreaId: number | null
  /** The sub-area's name when the booking was priced — "Zamalek". */
  subArea: string | null
  /** Its group's name — "Downtown" — or null when it was in no group. */
  group: string | null
  /** Only known on a live check (the Cairo areas page); false when read back from a booking. */
  primary: boolean
  surchargeMinor: number
  surcharge: number
}

/**
 * Where a Cairo booking's pickup and drop-off fell among the sub-areas drawn
 * on the Cairo areas page, and what each end added to the fare.
 *
 * Each group but the primary one carries a surcharge, and a trip pays it once
 * per end in that group: East → West is East's plus West's. Nested on trips and
 * trip requests like `addOns`, and always present — anything that is not a
 * Cairo booking, or was booked before sub-areas or while Cairo was priced as
 * one area, arrives as `any: false` with zeros.
 *
 * A breakdown of the fare, not an addition to it: the app showed the customer
 * each package with the surcharge already in its price, so `basePrice` and
 * `payment.tripAmount` include it. The customer never sees the breakdown.
 */
export interface AreaSurcharge {
  /** Either end fell in a sub-area — worth showing even when it added nothing. */
  any: boolean
  pickup: AreaSurchargeEnd
  dropoff: AreaSurchargeEnd
  totalMinor: number
  total: number
  currency: string
  /** "Nasr City (East Cairo) → Zamalek (Downtown) · +80.00 EGP", or null when `any` is false. */
  summary: string | null
}

export interface AdminTripRequest {
  id: number
  bookingReference: string | null
  userId: number | null
  userName: string | null
  userPhone: string | null
  driverId: number | null
  driverName: string | null
  driverPhone: string | null
  categoryName: string | null
  serviceType: string | null
  packageName: string | null
  pickupAddress: string | null
  /**
   * Where the journey actually is, for the route map.
   *
   * Null when the booking never carried a coordinate — the drop-off half is
   * empty on open-ended TRAVEL bookings, and both halves are on rows written
   * before the app sent them. The server sends null rather than the 0.0 its
   * primitive columns hold, so an unset pair cannot be mistaken for a real
   * point in the Atlantic.
   */
  pickupLatitude: number | null
  pickupLongitude: number | null
  dropoffAddress: string | null
  dropoffLatitude: number | null
  dropoffLongitude: number | null
  status: string | null
  paymentStatus: string | null
  paymentMethod: string | null
  basePrice: number | null
  carModel: string | null
  transmissionMode: string | null
  startTime: string | null
  endTime: string | null
  scheduledAt: string | null
  createdAt: string | null
  /** The Paymob charge behind this booking, when there is one. */
  paymobTransactionId: number | null
  /**
   * Cheap hint that a refund is worth asking about — it does not authorise one.
   * The Refund action is still gated on the server's eligibility endpoint.
   */
  refundCandidate: boolean
  /**
   * Whether to offer the Cancel action. True for a confirmed booking as well as
   * a pending one — the confirmed ones with a driver already assigned are
   * exactly the bookings customers ring up to cancel.
   */
  canCancel: boolean
  /** Whether the pickup and finish times may still be moved. */
  canModifySchedule: boolean
  /**
   * Why this booking's payment stops a driver being assigned, or null when
   * nothing does.
   *
   * A sentence rather than a boolean, and the same sentence the API throws if
   * the assignment is attempted anyway. An operator looking at a booking they
   * cannot dispatch needs to know which reason it is, because each has a
   * different fix: an Instapay booking wants its receipt attached, a pending
   * card booking wants switching to Instapay, and a refunded one wants leaving
   * alone.
   *
   * Says nothing about whether the request is still pending — that is `status`,
   * which is the existing gate on the Approve action. This covers the payment.
   */
  assignmentBlockedReason: string | null
  /** How this booking was paid for. Always present, including on legacy rows. */
  payment: AdminPaymentSummary
  /** The return leg and extra hours chosen, if any. Always present. */
  addOns: TravelAddOns
  /** The Cairo sub-areas the two ends fell in and what they added. Always present. */
  areaSurcharge: AreaSurcharge
}

export interface TripRequestFilters extends PageParams {
  search?: string
  status?: string
  serviceType?: string
  paymentStatus?: string
  paymentMethod?: string
  verificationStatus?: string
  from?: string
  to?: string
}

/* ------------------------------------------------------------------- trips */

export const TRIP_STATUSES = [
  'PENDING',
  'CONFIRMED',
  'ON_THE_WAY',
  'ARRIVED',
  'STARTED',
  'COMPLETED',
  'CANCELLED',
] as const
export type TripStatus = (typeof TRIP_STATUSES)[number]

export const OTS_SYNC_STATES = ['PENDING', 'PROCESSING', 'SYNCED', 'FAILED'] as const
export type OtsSyncState = (typeof OTS_SYNC_STATES)[number]

/**
 * Whether a trip has reached the driver's OTS app. The trip is created there
 * when a driver is assigned; every later change (times, driver, cancellation)
 * is sent again. Null on a trip that was never sent — from before the
 * integration, or with it switched off.
 */
export interface OtsSyncStatus {
  state: OtsSyncState | null
  otsTripId: number | null
  /** The OTS trip status: 0 assigned, 1 ongoing, 2 ended, -1 cancelled. */
  otsStatus: number | null
  attempts: number
  lastAttemptAt: string | null
  nextAttemptAt: string | null
  syncedAt: string | null
  lastErrorCode: string | null
  /** Why the last attempt failed, in operator wording. */
  lastError: string | null
  /** A change is queued that the driver app has not received yet. */
  outstanding: boolean
}

export interface AdminTrip {
  id: number
  requestId: number | null
  bookingReference: string | null
  userId: number | null
  userName: string | null
  userPhone: string | null
  driverId: number | null
  driverName: string | null
  driverPhone: string | null
  driverImageUrl: string | null
  categoryName: string | null
  serviceType: string | null
  packageName: string | null
  pickupAddress: string | null
  pickupLatitude: number | null
  pickupLongitude: number | null
  dropoffAddress: string | null
  dropoffLatitude: number | null
  dropoffLongitude: number | null
  status: string | null
  startTime: string | null
  startedAt: string | null
  endTime: string | null
  endedAt: string | null
  driverArrivedAt: string | null
  createdAt: string | null
  carModel: string | null
  transmissionMode: string | null
  paymentMethod: string | null
  basePaymentStatus: string | null
  extraPaymentStatus: string | null
  basePrice: number | null
  extraHoursCost: number | null
  totalPrice: number | null
  extraHoursCount: number | null
  /** Server-computed lifecycle permissions — the dashboard never guesses. */
  canStart: boolean
  canEnd: boolean
  canModifyDriver: boolean
  canModifyTimes: boolean
  /** False once the journey is complete or already called off. */
  canCancel: boolean
  /** The Paymob charge behind this trip's booking, when there is one. */
  paymobTransactionId: number | null
  /** Whether to offer a Refund action. The eligibility endpoint is still the authority. */
  refundCandidate: boolean
  /**
   * How the booking behind this trip was paid for — the same record the Trip
   * Requests page renders, read from the same booking row. A trip has no
   * payment of its own.
   */
  payment: AdminPaymentSummary
  /** What was added to the route when this trip was booked, read from the booking behind it. */
  addOns: TravelAddOns
  /** Where in Cairo the booking's ends fell and what that added, read from the booking. */
  areaSurcharge: AreaSurcharge
  /** The trip's copy on the driver's OTS app. */
  driverApp: OtsSyncStatus | null
  /**
   * The driver entered a wrong start code too many times; the app will not
   * start the trip. An administrator's Start trip still works.
   */
  startCodeLocked: boolean
}

export interface TripFilters extends PageParams {
  search?: string
  status?: string
  driverId?: number
  paymentMethod?: string
  verificationStatus?: string
  from?: string
  to?: string
}

/* ------------------------------------------------------------- promo codes */

export const DISCOUNT_TYPES = ['PERCENTAGE', 'FIXED'] as const
export type DiscountType = (typeof DISCOUNT_TYPES)[number]

export const PROMO_STATES = ['ACTIVE', 'SCHEDULED', 'EXPIRED', 'DISABLED'] as const
export type PromoState = (typeof PROMO_STATES)[number]

export interface AdminPromo {
  id: number
  code: string
  description: string | null
  discountType: string | null
  discountValue: number | null
  maxDiscountAmount: number | null
  usageLimitTotal: number | null
  usageLimitPerUser: number | null
  usedCount: number | null
  startDate: string | null
  endDate: string | null
  isActive: boolean
  state: string
  createdAt: string | null
  updatedAt: string | null
}

export interface PromoFilters extends PageParams {
  search?: string
  state?: string
  discountType?: string
}

/** Field names match the existing `/api/admin/promo/add` contract. */
export interface PromoCreateRequest {
  code: string
  description: string
  discountType: 'percentage' | 'fixed'
  discountValue: number
  maxDiscountValue: number
  usageLimitTotal: number
  usageLimitPerUser: number
  startDate: string
  endDate: string
}

export interface PromoUpdateRequest extends PromoCreateRequest {
  id: number
  isActive: boolean
}

/* --------------------------------------------------------------- dashboard */

export interface TimeSeriesPoint {
  date: string
  count: number
}

export interface DashboardStats {
  totalUsers: number
  activeUsers: number
  suspendedUsers: number
  totalDrivers: number
  activeDrivers: number
  suspendedDrivers: number
  pendingTripRequests: number
  confirmedTripRequests: number
  cancelledTripRequests: number
  activeTrips: number
  completedTrips: number
  cancelledTrips: number
  totalTrips: number
  activePromoCodes: number
  scheduledPromoCodes: number
  expiredPromoCodes: number
  disabledPromoCodes: number
  tripsByStatus: Record<string, number>
  tripRequestsByStatus: Record<string, number>
  tripsPerDay: TimeSeriesPoint[]
  signupsPerDay: TimeSeriesPoint[]
}

export const ACTIVITY_TYPES = ['TRIP', 'TRIP_REQUEST'] as const
export type ActivityType = (typeof ACTIVITY_TYPES)[number]

export interface ActivityEntry {
  type: ActivityType
  referenceId: number
  bookingReference: string | null
  title: string | null
  subtitle: string | null
  status: string | null
  occurredAt: string
}

/* --------------------------------------------------------------- reporting */

/** One bucket of the outcome trend; `date` is the day, or the first of the month. */
export interface ReportTrendPoint {
  date: string
  received: number
  fulfilled: number
  cancelled: number
  active: number
}

export interface ReportServiceTypeStat {
  type: ServiceType
  received: number
  fulfilled: number
  cancelled: number
}

/** All money in integer minor units (EGP piastres) — divide by 100 only to display. */
export interface BookingReportMoney {
  fulfilledRevenueMinor: number
  cashRevenueMinor: number
  cardRevenueMinor: number
  refundsPaidMinor: number
  refundsCount: number
  outstandingPenaltiesMinor: number
  outstandingPenaltiesCount: number
  pointsSpentMinor: number
}

export const REPORT_TREND_GRANULARITIES = ['DAY', 'MONTH'] as const
export type ReportTrendGranularity = (typeof REPORT_TREND_GRANULARITIES)[number]

/** One band of the overall response-time histogram, e.g. `{ label: '< 5m', count: 12 }`. */
export interface ResponseTimeBucket {
  label: string
  count: number
}

/**
 * One employee's answered requests in the window. `adminUserId`/`adminName` are
 * null only for the rare unattributed row; `averageSeconds`/`medianSeconds` are
 * null when they answered nothing (which never happens for a row that exists).
 */
export interface EmployeeResponseStat {
  adminUserId: number | null
  adminName: string | null
  respondedCount: number
  assignedCount: number
  rejectedCount: number
  averageSeconds: number | null
  medianSeconds: number | null
}

/**
 * How quickly requests were answered — a driver assigned, or a pending request
 * rejected by a supervisor — overall and per employee.
 *
 * All durations are whole **seconds**; the browser formats them and never does
 * arithmetic. Unlike the rest of {@link BookingReport} this is a cohort on
 * **when each request was answered**, not when the booking was received, and it
 * is grouped by the employee who answered. `respondedCount` equals
 * `assignedCount + rejectedCount` and the sum of every employee's
 * `respondedCount`. The averages are null when nothing was answered in the
 * window.
 */
export interface ResponseTimeReport {
  respondedCount: number
  assignedCount: number
  rejectedCount: number
  averageSeconds: number | null
  medianSeconds: number | null
  p90Seconds: number | null
  distribution: ResponseTimeBucket[]
  perEmployee: EmployeeResponseStat[]
}

/**
 * The Reporting screen's payload. `from` is null for an all-time report; the
 * outcome counts reconcile as `received == fulfilled + cancelledTotal + active`,
 * and the three cancellation figures sum to `cancelledTotal`.
 */
export interface BookingReport {
  from: string | null
  to: string
  received: number
  fulfilled: number
  active: number
  confirmed: number
  cancelledTotal: number
  cancelledByCustomer: number
  cancelledByStaff: number
  cancelledBySystem: number
  fulfillmentRate: number
  cancellationRate: number
  serviceTypes: ReportServiceTypeStat[]
  trendGranularity: ReportTrendGranularity
  trend: ReportTrendPoint[]
  money: BookingReportMoney
  responseTime: ResponseTimeReport
}

/* ----------------------------------------------------------------- refunds */

export const REFUND_STATUSES = [
  'REQUESTED',
  'PROCESSING',
  'SUCCEEDED',
  'FAILED',
  'PENDING',
  'REQUIRES_RECONCILIATION',
] as const
export type RefundStatus = (typeof REFUND_STATUSES)[number]

export const REFUND_SOURCES = ['MANUAL_ADMIN', 'TRIP_CANCELLATION', 'CARD_VERIFICATION'] as const
export type RefundSource = (typeof REFUND_SOURCES)[number]

/**
 * Stable machine codes the API returns when a refund is refused.
 * Mirrors `RefundException.Code` on the backend.
 */
export const REFUND_ERROR_CODES = [
  'NOT_REFUNDABLE',
  'ALREADY_FULLY_REFUNDED',
  'REFUND_ALREADY_PROCESSING',
  'AMOUNT_EXCEEDS_REMAINING',
  'INVALID_AMOUNT',
  'INVALID_REASON',
  'UNSUPPORTED_PAYMENT_METHOD',
  'MISSING_TRANSACTION_ID',
  'PAYMENT_NOT_CAPTURED',
  'PAYMOB_REJECTED',
  'GATEWAY_UNCERTAIN',
  'RECORD_NOT_FOUND',
  'UNAUTHORIZED',
  'FORBIDDEN',
] as const
export type RefundErrorCode = (typeof REFUND_ERROR_CODES)[number]

/**
 * One refund attempt.
 *
 * Amounts are integer minor units (EGP piastres), exactly as stored and as sent
 * to Paymob. The browser formats them; it never does the arithmetic, so no
 * rounding can creep in between the ledger and the screen.
 */
export interface AdminRefund {
  id: number
  idempotencyKey: string

  paymobTransactionId: number | null
  paymobOrderId: number | null
  paymobRefundTransactionId: number | null

  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  customerUserId: number | null
  customerName: string | null

  originalAmountMinor: number
  requestedAmountMinor: number
  refundedAmountMinor: number | null
  currency: string
  paymentMethod: string | null

  reason: string
  adminNote: string | null
  source: string | null
  status: string | null

  adminUserId: number | null
  adminName: string | null
  adminEmail: string | null

  attemptCount: number
  gatewayHttpStatus: number | null
  gatewayResponseCode: string | null
  gatewayMessage: string | null
  gatewayMetadata: string | null

  createdAt: string | null
  updatedAt: string | null
  requestedAt: string | null
  completedAt: string | null
  lastReconciledAt: string | null

  /** True while the attempt still needs a human or a callback to settle it. */
  needsAttention: boolean
}

export interface RefundFilters extends PageParams {
  search?: string
  status?: string
  source?: string
  adminUserId?: number
  paymobTransactionId?: number
  bookingReference?: string
  from?: string
  to?: string
}

export interface RefundSummary {
  succeededAmountMinor: number
  succeededCount: number
  pendingAmountMinor: number
  pendingCount: number
  failedAmountMinor: number
  failedCount: number
  needsAttentionCount: number
  totalCount: number
  currency: string
}

/**
 * The server's verdict on whether a booking can be refunded.
 * The dashboard never derives this from status strings of its own.
 */
export interface RefundEligibility {
  canRefund: boolean
  refundBlockedReason: string | null
  refundBlockedCode: string | null

  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  customerUserId: number | null
  customerName: string | null
  customerPhone: string | null

  paymobTransactionId: number | null
  paymobOrderId: number | null
  paymentMethod: string | null
  paymentStatus: string | null
  currency: string

  /**
   * What the customer actually paid outside points:
   * `tripAmountMinor + settledPenaltyAmountMinor - pointsUsedAmountMinor`.
   *
   * This is the refundable ceiling. Points were never money leaving the
   * customer's bank, so returning them is the separate restore path and
   * counting them here would let an operator refund value that was never
   * transferred.
   */
  originalPaidAmountMinor: number
  /**
   * The fare alone, after any promo discount. The cancellation policy's
   * percentage applies to this rather than to the total — a booking that also
   * cleared an old cancellation penalty must not have that debt handed back by
   * an automatic tier.
   */
  tripAmountMinor: number
  /** Zero for every booking that did not clear an earlier penalty, which is almost all of them. */
  settledPenaltyAmountMinor: number
  /** Zero for every booking made before points existed. */
  pointsUsedAmountMinor: number
  successfullyRefundedAmountMinor: number
  pendingRefundAmountMinor: number
  remainingRefundableAmountMinor: number

  /**
   * How an approved refund actually reaches the customer.
   *
   * `GATEWAY` reverses a Paymob charge. `POINTS` credits application points,
   * which is the only way to return an Instapay transfer — a bank transfer
   * cannot be sent back the way it arrived. `NONE` when nothing is refundable.
   *
   * The dialog words its confirmation from this, so a supervisor is told the
   * customer will receive points *before* they approve rather than after.
   */
  refundSettlementChannel: RefundSettlementChannel
  partialRefundSupported: boolean
  existingRefunds: AdminRefund[]
}

export const REFUND_SETTLEMENT_CHANNELS = ['GATEWAY', 'POINTS', 'NONE'] as const
export type RefundSettlementChannel = (typeof REFUND_SETTLEMENT_CHANNELS)[number]

/* ----------------------------------------------------------- cancellation */

export const CANCELLATION_BANDS = ['FULL', 'HALF', 'NONE'] as const
export type CancellationBand = (typeof CANCELLATION_BANDS)[number]

export type RefundMode = 'FULL' | 'PARTIAL' | 'NONE'
export type PenaltyMode = 'APPLY' | 'NONE'

/**
 * The server's answer to "what should this cancellation dialog look like, and
 * what should it default to?".
 *
 * `mode` decides the dialog's shape and is deliberately not a payment-method
 * string: a booking marked CARD whose payment never captured has nothing to
 * refund, and offering to refund it would offer something the server would then
 * refuse.
 */
export interface CancellationOptions {
  mode: 'REFUND' | 'PENALTY'
  cancellable: boolean
  blockedReason: string | null

  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  customerUserId: number | null
  customerName: string | null
  customerPhone: string | null
  serviceType: string | null
  paymentMethod: string | null
  paymentStatus: string | null
  currency: string

  scheduledPickupAt: string | null
  minutesUntilPickup: number | null
  /** False when the booking has no recorded pickup time — say so rather than showing a window. */
  scheduleKnown: boolean

  policyBand: CancellationBand
  policyRefundPercent: number
  /** What the policy would refund — the labelled default for a card booking. */
  policyRefundMinor: number
  /** What the policy would charge — the labelled default for a cash booking. */
  policyPenaltyMinor: number

  tripAmountMinor: number
  settledPenaltyAmountMinor: number
  originalPaidAmountMinor: number
  alreadyRefundedMinor: number
  pendingRefundMinor: number
  remainingRefundableMinor: number
  partialRefundSupported: boolean
}

/* ------------------------------------------------------------- rescheduling */

/**
 * An operator's edit to a booking's times.
 *
 * A field left out is one they did not touch, and the server leaves it alone —
 * so moving only the pickup does not require echoing back an end time. Both
 * values are Cairo wall clocks ("2026-08-14T10:30"), the same convention the
 * rest of the API uses.
 */
export interface RescheduleBookingRequest {
  startTime?: string
  endTime?: string
  reason?: string
}

/* --------------------------------------------------------- change history */

export const BOOKING_CHANGE_ACTIONS = ['SCHEDULE_CHANGED', 'DRIVER_REASSIGNED', 'CANCELLED'] as const
export type BookingChangeAction = (typeof BOOKING_CHANGE_ACTIONS)[number]

export const BOOKING_CHANGE_FIELDS = ['START_TIME', 'END_TIME', 'DRIVER', 'STATUS'] as const
export type BookingChangeField = (typeof BOOKING_CHANGE_FIELDS)[number]

/**
 * One operator change to a booking.
 *
 * `oldValue` and `newValue` arrive already formatted by the server and are
 * printed as they are — a time in this list is the operational wall clock the
 * operator saw, not an instant to re-render.
 */
export interface BookingChangeLogEntry {
  id: number
  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  action: string | null
  field: string | null
  oldValue: string | null
  newValue: string | null
  summary: string | null
  /** What the operator typed, where the action asked for a reason. */
  reason: string | null
  adminUserId: number | null
  adminName: string | null
  createdAt: string | null
}

export interface CancelBookingRequest {
  refundMode: RefundMode
  /** Minor units. Required when `refundMode` is `PARTIAL`. */
  refundAmountMinor?: number
  penaltyMode: PenaltyMode
  /** Minor units. Required when `penaltyMode` is `APPLY`. */
  penaltyAmountMinor?: number
  reason: string
}

/* --------------------------------------------------------------- penalties */

export const PENALTY_STATUSES = ['OUTSTANDING', 'PENDING_SETTLEMENT', 'SETTLED', 'WAIVED'] as const
export type PenaltyStatus = (typeof PENALTY_STATUSES)[number]

export const PENALTY_SOURCES = ['CUSTOMER_CANCELLATION', 'ADMIN_CANCELLATION'] as const
export type PenaltySource = (typeof PENALTY_SOURCES)[number]

/**
 * One cancellation penalty.
 *
 * Carries its whole explanation, not just an amount: which booking it came
 * from, which band produced it and how long before pickup the customer
 * cancelled. Operators get asked "why am I being charged 250?" and need to be
 * able to answer it.
 */
export interface CustomerPenalty {
  id: number
  userId: number | null
  customerName: string | null
  customerPhone: string | null

  amountMinor: number
  currency: string
  status: PenaltyStatus | null

  sourceTripRequestId: number | null
  sourceTripId: number | null
  sourceBookingReference: string | null
  bookingPriceMinor: number
  serviceType: string | null
  policyBand: string | null
  refundPercent: number
  minutesBeforePickup: number | null
  scheduledPickupAt: string | null
  reason: string | null

  createdBySource: PenaltySource | null
  createdByAdminUserId: number | null
  createdByAdminName: string | null

  settlingTripRequestId: number | null
  settledBookingReference: string | null
  settledPaymobTransactionId: number | null
  reservedAt: string | null
  settledAt: string | null

  waivedByAdminUserId: number | null
  waivedByAdminName: string | null
  waiveReason: string | null
  waivedAt: string | null

  createdAt: string | null
  updatedAt: string | null

  /** True while the amount still counts against the customer. */
  outstanding: boolean
  /** True when an operator could still waive it. */
  waivable: boolean
}

export interface PenaltyFilters extends PageParams {
  search?: string
  status?: string
  source?: string
  userId?: number
}

export interface WaivePenaltyRequest {
  reason: string
}

export interface RefundCreateRequest {
  tripRequestId: number
  /** Minor units. Omitted for a full refund of the remaining balance. */
  amountMinor?: number
  reason: string
  adminNote?: string
  idempotencyKey: string
}

export interface RefundAdminOption {
  id: number
  name: string | null
  email: string | null
}

/* -------------------------------------------------------------- trip edits */

/** The states a customer's change request moves through. */
export const TRIP_EDIT_STATUSES = [
  'PENDING_REVIEW',
  'AWAITING_PAYMENT',
  'PAYMENT_SUBMITTED',
  'APPLIED',
  'REJECTED',
  'WITHDRAWN',
] as const

export type TripEditStatus = (typeof TRIP_EDIT_STATUSES)[number]

/** Which part of the booking an edit touches. */
export const TRIP_EDIT_CHANGES = ['TIME', 'DURATION', 'LOCATION'] as const

export type TripEditChange = (typeof TRIP_EDIT_CHANGES)[number]

/**
 * One customer request to change a booking, as a supervisor sees it.
 *
 * Carries both sides of every change. "Is this worth more money?" cannot be
 * answered from the new value alone — an hour later is nothing, an hour later
 * in a different governorate is a different trip.
 *
 * `previous*` is the snapshot taken when the customer asked; `bookingStartTime`
 * is where the booking stands now. The two can legitimately disagree if the
 * office moved the booking while the request was queued, which is a situation
 * to show rather than hide.
 */
export interface AdminTripEdit {
  id: number
  tripRequestId: number
  tripId: number | null
  bookingReference: string | null
  status: TripEditStatus

  userId: number
  customerName: string | null
  customerPhone: string | null

  changes: TripEditChange[]

  requestedStartTime: string | null
  requestedPackageId: number | null
  requestedPackageName: string | null
  requestedPickupAddress: string | null
  requestedPickupLat: number | null
  requestedPickupLng: number | null
  requestedDropoffAddress: string | null
  requestedDropoffLat: number | null
  requestedDropoffLng: number | null
  customerNote: string | null

  previousStartTime: string | null
  previousEndTime: string | null
  previousPackageName: string | null
  previousPickupAddress: string | null
  previousDropoffAddress: string | null
  previousBasePrice: number | null

  bookingStartTime: string | null
  bookingStatus: string | null
  bookingPaymentMethod: string | null
  bookingPriceMinor: number
  /** Everything already charged for this booking, earlier edits included. */
  bookingTotalMinor: number

  extraAmountMinor: number
  currency: string

  decidedByAdminUserId: number | null
  decidedByAdminName: string | null
  decidedAt: string | null
  decisionNote: string | null

  paymentMethod: string | null
  paymobTransactionId: number | null
  paidAt: string | null
  hasProof: boolean
  proofUploadedAt: string | null

  verifiedByAdminUserId: number | null
  verifiedByAdminName: string | null
  verifiedAt: string | null

  /** True when this edit is waiting on a supervisor to decide it. */
  decidable: boolean
  /** True when this edit is waiting on a supervisor to confirm a transfer. */
  verifiable: boolean

  createdAt: string | null
  updatedAt: string | null

  /** The Cairo area surcharge the booking carries now. */
  areaSurcharge: AreaSurcharge
  /**
   * What the booking would carry with the requested pickup and drop-off, at
   * today's area prices. Null unless the edit is still open, moves a location
   * and is on a Cairo booking. Information for pricing the edit — never a charge
   * by itself.
   */
  requestedAreaSurcharge: AreaSurcharge | null
}

export interface TripEditFilters extends PageParams {
  search?: string
  /** A `TripEditStatus`, or `OPEN` for everything still waiting on a human. */
  status?: string
  change?: string
  from?: string
  to?: string
}

/**
 * The supervisor's answer.
 *
 * `extraAmount` is in EGP, which is what the supervisor types — the server
 * converts it once, so no browser has to know about minor units. Omitting it
 * approves the change at the price the booking already carries.
 */
export interface TripEditDecisionRequest {
  approve: boolean
  extraAmount?: number
  note?: string
}

/* ------------------------------------------------------------------ ratings */

/**
 * The reasons a customer can attach to a score. Mirrors `RatingTag` on the
 * server, which is the authority; the labels live in `features/ratings/describe`.
 */
export const RATING_TAGS = [
  'SAFE_DRIVING',
  'ON_TIME',
  'POLITE',
  'CAREFUL_WITH_CAR',
  'GREAT_ROUTE',
  'GOOD_COMMUNICATION',
  'LATE',
  'DRIVING_STYLE',
  'ATTITUDE',
  'ROUTE_TAKEN',
  'CAR_HANDLING',
  'POOR_COMMUNICATION',
] as const

export type RatingTag = (typeof RATING_TAGS)[number]

/** LOW is 1–2 stars, MID is 3, HIGH is 4–5 — the three questions a supervisor asks. */
export const RATING_BANDS = ['LOW', 'MID', 'HIGH'] as const

export type RatingBand = (typeof RATING_BANDS)[number]

/**
 * One customer rating, with the trip it is about.
 *
 * "Two stars" on its own is a number; two stars on Thursday's airport run with
 * "late" ticked is something a supervisor can act on.
 */
export interface AdminRating {
  id: number
  tripId: number | null
  tripRequestId: number | null
  bookingReference: string | null

  userId: number | null
  customerName: string | null
  customerPhone: string | null

  driverId: number | null
  driverName: string | null
  driverImageUrl: string | null

  rating: number
  /** `RatingTag` codes; a code this build does not know is shown as-is. */
  tags: string[]
  comment: string | null
  /** True at or below the server's low threshold — the ones to look at. */
  low: boolean

  pickupAddress: string | null
  dropoffAddress: string | null
  packageName: string | null
  tripStartTime: string | null
  tripEndedAt: string | null
  tripTotalPrice: number | null

  createdAt: string | null
}

export interface RatingTagCount {
  tag: string
  positive: boolean
  count: number
}

/**
 * The headline figures over the same filter as the list, so the number at the
 * top and the rows underneath can never be about different trips.
 */
export interface RatingSummary {
  ratingCount: number
  /** Null when nothing to average. */
  average: number | null
  /** Keyed "1".."5", always all five. */
  distribution: Record<string, number>
  lowCount: number
  withCommentCount: number
  /** Trips that finished in the window, rated or not. */
  completedTripCount: number
  /** `ratingCount / completedTripCount`, or null when nothing finished. */
  responseRate: number | null
  /** Most common first. */
  tags: RatingTagCount[]
}

/** One driver's standing, for the leaderboard. */
export interface DriverRatingStats {
  driverId: number
  driverName: string | null
  driverImageUrl: string | null
  enabled: boolean
  ratingCount: number
  average: number | null
  lowCount: number
  fiveStarCount: number
  /** The reasons customers gave most often, up to three. */
  topTags: string[]
}

export interface RatingFilters extends PageParams {
  search?: string
  /** An exact score, 1–5. Takes precedence over `band`. */
  rating?: number
  band?: string
  driverId?: number
  tag?: string
  hasComment?: boolean
  from?: string
  to?: string
}

/* ------------------------------------------------------------ area pricing */

/** A map position, latitude first — the order Leaflet and every coordinate here use. */
export interface GeoPoint {
  lat: number
  lng: number
}

/**
 * A group of Cairo sub-areas — Downtown, East Cairo — and what each end of a
 * trip in it adds. The primary group adds nothing; every other group's
 * surcharge is what it costs to reach the primary group from there.
 */
export interface AreaGroup {
  id: number
  name: string
  /** EGP before VAT, per end of the trip. Always 0 on the primary group. */
  surcharge: number
  surchargeMinor: number
  primary: boolean
  subAreaCount: number
  updatedAt: string | null
  updatedByAdminName: string | null
}

/** A piece of Cairo drawn on the map. */
export interface SubArea {
  id: number
  name: string
  /** Null while it is in no group — it then adds nothing. */
  groupId: number | null
  groupName: string | null
  /** An open ring: the first corner is not repeated at the end. */
  boundary: GeoPoint[]
  areaKm2: number
  updatedAt: string | null
  updatedByAdminName: string | null
}

/** The rectangle that counts as Cairo. Every sub-area has to fit inside it. */
export interface ServiceAreaBounds {
  minLat: number
  minLng: number
  maxLat: number
  maxLng: number
}

export interface AreaPricingOverview {
  /** Whether Cairo bookings are being charged the surcharges. */
  enabled: boolean
  updatedAt: string | null
  updatedByAdminName: string | null
  serviceArea: ServiceAreaBounds
  currency: string
  /** The primary group first, then by name. */
  groups: AreaGroup[]
  /** By name. */
  subAreas: SubArea[]
  /**
   * What a trip from each group to each group adds — every ordered pair, worked
   * out by the server with the rule bookings are priced by. The page's price
   * table reads these; it never adds two surcharges itself.
   */
  pairs: AreaGroupPair[]
}

export interface AreaGroupPair {
  pickupGroupId: number
  dropoffGroupId: number
  totalMinor: number
  total: number
}

export interface AreaGroupInput {
  name: string
  /** EGP before VAT. Ignored, and stored as 0, on a primary group. */
  surcharge: number
  /** Making a group primary takes the mark off the group that had it. */
  primary: boolean
}

export interface SubAreaInput {
  name: string
  groupId: number | null
  boundary: GeoPoint[]
}

/* --------------------------------------------------------------- mutations */

export interface DriverCreateRequest {
  name: string
  email: string
  phone: string
  password: string
  profileImageUrl?: string
}

export interface MessageResponse {
  message: string
}

export interface UploadResponse {
  url: string
}
